import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MaturityScorecard.css';
import CategoryInsightModal from '../modals/CategoryInsightModal';

const MaturityScorecard = ({ assessment, assessmentData, styles }) => {
  // Support both assessment and assessmentData props for backward compatibility
  const data = assessment || assessmentData || {};
  const { t } = useTranslation();
  
  // State for the category insight modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Handle category cell click
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setModalOpen(true);
  };
  
  // Get organization name if available
  const organizationName = data.organizationName || 'Your Organization';
  
  // Define maturity levels with standard risk assessment colors (softer tones)
  const maturityLevels = [
    { level: 0, name: 'None', color: '#6C757D', textColor: '#FFFFFF', range: [0, 1] },       // Gray
    { level: 1, name: 'Initial', color: '#DC3545', textColor: '#FFFFFF', range: [1, 20] },    // Red
    { level: 2, name: 'Defined', color: '#E67E22', textColor: '#FFFFFF', range: [20, 50] },   // Softer Orange
    { level: 3, name: 'Managed', color: '#F4D03F', textColor: '#243444', range: [50, 75] },   // Softer Yellow with navy text
    { level: 4, name: 'Compliant', color: '#0D6EFD', textColor: '#FFFFFF', range: [75, 91] }, // Blue
    { level: 5, name: 'Optimized', color: '#198754', textColor: '#FFFFFF', range: [91, 101] } // Green
  ];
  
  // Define component styles
  const scorecardStyles = {
    // Category cell styles for clickable behavior
    clickableCategory: {
      cursor: 'pointer',
      position: 'relative',
      transition: 'background-color 0.3s',
      backgroundColor: '#f9f9f9',
      '&:hover': {
        backgroundColor: '#f0f0f0'
      }
    },
    aiIndicator: {
      position: 'absolute',
      top: '50%',
      right: '10px',
      transform: 'translateY(-50%)',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      backgroundColor: '#D2BA7B',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '10px',
      color: '#243444',
      fontWeight: 'bold'
    },
    // PDF header styles
    pdfHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 20px',
      backgroundColor: '#FFFAFA',
      borderBottom: '1px solid #eee',
      marginBottom: '20px',
      position: 'running(header)'
    },
    headerLogo: {
      height: '40px',
      width: 'auto'
    },
    headerText: {
      color: '#243444',
      fontSize: '12px',
      fontWeight: 'normal'
    },
    // Cover page styles
    coverPage: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '1050px', // Fixed height for US Letter page (11 inches at 96dpi)
      padding: '40px',
      backgroundColor: '#FFFAFA',
      color: '#243444',
      pageBreakAfter: 'always',
      textAlign: 'center',
      position: 'relative'
    },
    coverPageFooter: {
      position: 'absolute',
      bottom: '40px',
      left: 0,
      right: 0,
      textAlign: 'center',
      fontSize: '12px',
      color: '#4C5454'
    },
    coverLogo: {
      width: '200px', // Smaller logo
      marginBottom: '30px'
    },
    coverTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#243444',
      marginBottom: '20px',
      textAlign: 'center'
    },
    coverSubtitle: {
      fontSize: '24px',
      color: '#4C5454',
      marginBottom: '40px',
      textAlign: 'center'
    },
    coverDate: {
      fontSize: '18px',
      color: '#4C5454',
      marginTop: '40px',
      textAlign: 'center'
    },
    coverOrg: {
      fontSize: '22px',
      color: '#D2BA7B',
      fontWeight: 'bold',
      marginBottom: '10px',
      textAlign: 'center'
    },
    // Table of contents styles
    tocPage: {
      padding: '40px',
      backgroundColor: '#FFFAFA',
      pageBreakAfter: 'always',
      pageBreakBefore: 'always',
      height: '1050px', // Fixed height for US Letter page (11 inches at 96dpi)
      position: 'relative'
    },
    tocTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#243444',
      marginBottom: '30px',
      textAlign: 'center'
    },
    tocSection: {
      marginBottom: '20px'
    },
    tocItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px dotted #D2BA7B'
    },
    tocText: {
      color: '#243444',
      fontSize: '16px'
    },
    tocPage: {
      color: '#4C5454',
      fontSize: '16px'
    },
    container: {
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '15px',
      pageBreakInside: 'avoid',
      pageBreakBefore: 'always',
      width: '100%'
    },
    header: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: '15px',
      textAlign: 'center',
      pageBreakInside: 'avoid',
      pageBreakAfter: 'avoid'
    },
    headerTitle: {
      color: '#243444',
      marginBottom: '15px',
      fontSize: '20px'
    },
    pageInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      marginBottom: '15px',
      padding: '10px',
      backgroundColor: '#f9f9f9',
      borderRadius: '5px'
    },
    infoContent: {
      flex: 1,
      textAlign: 'left',
      paddingLeft: '10px'
    },
    logoContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: '15px'
    },
    logo: {
      maxWidth: '100px',
      height: 'auto'
    },
    overallScore: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px'
    },
    scoreCircle: {
      width: '70px',
      height: '70px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#FFFAFA',
      fontSize: '22px',
      fontWeight: 'bold'
    },
    scoreDetails: {
      textAlign: 'left'
    },
    scoreDetailsTitle: {
      color: '#243444',
      margin: '0 0 3px 0',
      fontSize: '16px'
    },
    maturityLevel: {
      color: '#4C5454',
      fontSize: '16px',
      fontWeight: 'bold',
      margin: '0 0 3px 0'
    },
    assessmentDate: {
      color: '#4C5454',
      fontSize: '12px',
      margin: '0'
    },
    tableContainer: {
      overflowX: 'auto',
      marginBottom: '15px',
      pageBreakInside: 'avoid',
      pageBreakBefore: 'avoid'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      borderSpacing: '0',
      tableLayout: 'fixed'
    },
    th: {
      padding: '8px 10px',
      textAlign: 'center',
      border: '1px solid #eee',
      backgroundColor: '#243444',
      color: '#D2BA7B',
      fontSize: '14px'
    },
    td: {
      padding: '8px 10px',
      textAlign: 'center',
      border: '1px solid #eee',
      fontSize: '14px'
    },
    categoryHeader: {
      backgroundColor: '#243444',
      color: '#D2BA7B',
      textAlign: 'left',
      width: '180px'
    },
    maturityHeader: {
      width: '90px',
      backgroundColor: '#243444',
      color: '#D2BA7B'
    },
    scoreHeader: {
      backgroundColor: '#243444',
      color: '#D2BA7B',
      width: '70px'
    },
    categoryCell: {
      textAlign: 'left',
      fontWeight: 'bold',
      color: '#243444',
      backgroundColor: '#f9f9f9'
    },
    maturityCell: {
      fontSize: '20px'
    },
    scoreCell: {
      fontSize: '16px'
    },
    legend: {
      marginTop: '15px',
      padding: '15px',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px'
    },
    legendTitle: {
      color: '#243444',
      fontSize: '16px',
      marginBottom: '10px',
      textAlign: 'center'
    },
    legendItems: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '10px'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '5px',
      padding: '5px',
      borderRadius: '4px',
      backgroundColor: '#fff'
    },
    legendColor: {
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      marginRight: '8px'
    },
    legendText: {
      display: 'flex',
      flexDirection: 'column'
    },
    legendTextTitle: {
      fontSize: '14px',
      color: '#243444'
    },
    legendTextRange: {
      fontSize: '12px',
      color: '#4C5454'
    }
  };
  
  // Extract data from assessment
  let { categoryScores = {}, score = 0, date } = data;
  
  // Ensure score is a number between 0 and 1
  if (typeof score === 'string') {
    score = parseFloat(score) / 100; // Convert percentage string to decimal
  } else if (score > 1) {
    score = score / 100; // Convert percentage to decimal if needed
  }
  
  // Process category scores to ensure they're in the right format
  Object.keys(categoryScores).forEach(category => {
    let catScore = categoryScores[category];
    // Convert to decimal if it's a percentage
    if (catScore > 1) {
      categoryScores[category] = catScore / 100;
    }
  });
  
  // Format date if available
  const formattedDate = date ? new Date(date).toLocaleDateString() : new Date().toLocaleDateString();
  
  // Helper function to normalize scores correctly
  const normalizeScore = (score) => {
    if (score === undefined || score === null) return 0;
    
    // For very low scores (0-1%), ensure they don't get rounded to 100%
    if (score >= 0 && score < 0.01) {
      return Math.max(Math.round(score * 100), 0);
    }
    
    // Handle scores stored as decimals (0-1 range) or as percentages (1-100 range)
    // If score is exactly 1, it's 100%
    if (score === 1) {
      return 100;
    }
    
    // Normal score normalization
    return score < 1 ? Math.round(score * 100) : Math.round(score);
  };
  
  // Determine maturity level based on score
  const getMaturityLevelForScore = (score) => {
    const scorePercentage = normalizeScore(score);
    
    // Special case for very low scores (0-1%)
    if (scorePercentage >= 0 && scorePercentage < 1) {
      return maturityLevels[0]; // 'None' level
    }
    
    return maturityLevels.find(level => 
      scorePercentage >= level.range[0] && scorePercentage < level.range[1]
    ) || maturityLevels[0];
  };
  
  // Get color for a specific score
  const getColorForScore = (score) => {
    return getMaturityLevelForScore(score).color;
  };
  
  // Get text color for a specific background color for proper contrast
  const getTextColorForBackground = (levelIndex) => {
    // Use the textColor property from the maturity level
    if (maturityLevels[levelIndex] && maturityLevels[levelIndex].textColor) {
      return maturityLevels[levelIndex].textColor;
    }
    // Fallback for backward compatibility
    if (levelIndex === 0 || levelIndex === 1 || levelIndex === 4 || levelIndex === 5) {
      return '#FFFAFA'; // White text for dark backgrounds
    }
    return '#243444'; // Navy text for light backgrounds
  };
  
  // Get text color for a background color
  const getTextColorForBackgroundColor = (backgroundColor) => {
    // Yellow gets navy text
    if (backgroundColor === '#F4D03F') {
      return '#243444';
    }
    // Dark backgrounds get white text
    const darkColors = ['#198754', '#0D6EFD', '#DC3545', '#6C757D', '#E67E22'];
    if (darkColors.includes(backgroundColor)) {
      return '#FFFFFF';
    }
    // Default
    return '#243444';
  };
  
  // Get gradient background for a cell
  const getCellBackground = (categoryScore, levelIndex) => {
    const scorePercentage = Math.round(categoryScore * 100);
    const level = maturityLevels[levelIndex];
    
    // If the score is in this level's range, apply the color
    if (scorePercentage >= level.range[0] && scorePercentage < level.range[1]) {
      return {
        backgroundColor: level.color,
        color: getTextColorForBackground(levelIndex)
      };
    }
    
    // If the score is higher than this level, fill completely
    if (scorePercentage >= level.range[1]) {
      return {
        backgroundColor: level.color,
        opacity: 0.7,
        color: getTextColorForBackground(levelIndex)
      };
    }
    
    // Otherwise, no background
    return {
      backgroundColor: 'transparent'
    };
  };
  
  // Calculate the width percentage for the progress bar
  const calculateProgressWidth = (score, levelIndex) => {
    const scorePercentage = Math.round(score * 100);
    const level = maturityLevels[levelIndex];
    const levelRange = level.range[1] - level.range[0];
    
    // If score is below this level's range, no progress
    if (scorePercentage < level.range[0]) {
      return 0;
    }
    
    // If score is above this level's range, full progress
    if (scorePercentage >= level.range[1]) {
      return 100;
    }
    
    // Calculate progress within this level's range
    const scoreWithinLevel = scorePercentage - level.range[0];
    return (scoreWithinLevel / levelRange) * 100;
  };
  
  // Determine if a level should show a progress bar
  const shouldShowProgressBar = (score, levelIndex) => {
    const scorePercentage = Math.round(score * 100);
    const level = maturityLevels[levelIndex];
    
    // Show progress bar if score is within or above this level's range
    return scorePercentage >= level.range[0];
  };
  
  // Define total page count for footer
  const totalPages = 39;
  
  // Create a separate component for the cover page to ensure it's isolated
  const CoverPageContent = () => (
    <div style={{
      textAlign: 'center',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      position: 'relative',
      maxHeight: '650px' // Ensure content fits within the container
    }}>
      <h1 style={{ fontSize: '32px', color: '#243444', marginBottom: '20px' }}>
        {data.industryType || 'Universal'} Maturity Scorecard Report
      </h1>
      <h2 style={{ fontSize: '26px', color: '#4C5454', marginBottom: '15px' }}>
        for
      </h2>
      <h3 style={{ fontSize: '36px', color: '#D2BA7B', fontWeight: 'bold', marginBottom: '30px' }}>
        {organizationName}
      </h3>
      
      <div style={{
        position: 'absolute',
        bottom: '40px',
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: '12px',
        color: '#4C5454'
      }}>
        <p>Page 1 of 39</p>
      </div>
    </div>
  );
  
  return (
    <div className="scorecard-pdf-document">
      {/* Cover Page with assessment type and organization name */}
      <div style={{
        height: '700px', // Further reduced height to fit on a single portrait letter page (8.5" x 11")
        width: '100%',
        backgroundColor: '#FFFAFA',
        margin: '0',
        padding: '0',
        pageBreakAfter: 'always',
        breakAfter: 'page',
        overflow: 'hidden', // Prevent content from overflowing
        position: 'relative',
        boxSizing: 'border-box'
      }} className="cover-page-container">
        <CoverPageContent />
      </div>
      
      {/* Explicit page break after cover page */}
      <div style={{
        display: 'block',
        height: '0',
        margin: '0',
        padding: '0',
        pageBreakBefore: 'always',
        pageBreakAfter: 'avoid',
        breakBefore: 'page',
        breakAfter: 'avoid'
      }}></div>
      <div style={scorecardStyles.tocPage} className="table-of-contents">
        <h2 style={scorecardStyles.tocTitle}>{t('reports.tableOfContents')}</h2>
        
        <div style={scorecardStyles.tocSection}>
          <div style={scorecardStyles.tocItem}>
            <span style={scorecardStyles.tocText}>Maturity Scorecard</span>
            <span style={scorecardStyles.tocPage}>3</span>
          </div>
          <div style={scorecardStyles.tocItem}>
            <span style={scorecardStyles.tocText}>Maturity Levels</span>
            <span style={scorecardStyles.tocPage}>3</span>
          </div>
          <div style={scorecardStyles.tocItem}>
            <span style={scorecardStyles.tocText}>Questions, Answers, and Notes</span>
            <span style={scorecardStyles.tocPage}>4</span>
          </div>
        </div>
        
        <div style={scorecardStyles.coverPageFooter}>
          <p>Page 2 of 39</p>
        </div>
      </div>
      
      {/* Removed 'How to Read This Scorecard' section from the top */}
      
      {/* Main Scorecard Content */}
      <div style={styles?.container || scorecardStyles.container}>
        {/* Category Insight Modal */}
        {modalOpen && selectedCategory && (
          <CategoryInsightModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            category={selectedCategory}
            assessment={data}
            initialPosition={{ x: 100, y: 100 }}
          />
        )}
        
        <div style={styles?.header || scorecardStyles.header}>
          <h2 style={styles?.title || scorecardStyles.title}>{t('reports.maturityScorecard')}</h2>
        </div>
        
        <div style={scorecardStyles.overallScore} className="overall-score">
          <div 
            style={{
              ...scorecardStyles.scoreCircle,
              backgroundColor: getColorForScore(score * 100),
              color: getTextColorForBackgroundColor(getColorForScore(score * 100))
            }}
          >
            {Math.round(score * 100)}%
          </div>
          
          <div style={scorecardStyles.scoreDetails}>
            <h3 style={scorecardStyles.scoreDetailsTitle}>{t('reports.overallMaturity')}</h3>
            <p style={scorecardStyles.maturityLevel}>
              {getMaturityLevelForScore(score * 100).name}
            </p>
            <p style={scorecardStyles.assessmentDate}>
              {t('reports.assessedOn')}: {formattedDate}
            </p>
          </div>
        </div>
      </div>
      
      <div style={scorecardStyles.tableContainer} className="scorecard-table-container">
        <table style={scorecardStyles.table} className="scorecard-table">
          <thead>
            <tr>
              <th style={{...scorecardStyles.th, ...scorecardStyles.categoryHeader}}>{t('reports.category')}</th>
              {maturityLevels.map((level) => (
                <th 
                  key={level.level}
                  style={{...scorecardStyles.th, ...scorecardStyles.maturityHeader}}
                >
                  {level.name}
                </th>
              ))}
              <th style={{...scorecardStyles.th, ...scorecardStyles.scoreHeader}}>{t('reports.score')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(categoryScores).map(([category, score]) => {
              const scorePercentage = normalizeScore(score);
              return (
                <tr key={category} className="scorecard-row">
                  <td 
                    className="clickable-category"
                    style={{
                      ...scorecardStyles.td, 
                      ...scorecardStyles.categoryCell
                    }}
                    onClick={() => handleCategoryClick({
                      name: category,
                      score: score
                    })}
                    title={`Click for AI analysis of ${category}`}
                  >
                    {category}
                  </td>
                  {maturityLevels.map((level) => {
                    const cellStyle = getCellBackground(score, level.level);
                    const progressWidth = calculateProgressWidth(score, level.level);
                    const isActiveLevel = scorePercentage >= level.range[0] && scorePercentage < level.range[1];
                    
                    return (
                      <td 
                        key={`${category}-${level.level}`} 
                        style={{
                          ...scorecardStyles.td, 
                          ...scorecardStyles.maturityCell,
                          position: 'relative',
                          padding: '12px 15px',
                          backgroundColor: '#f5f5f5'
                        }}
                      >
                        {/* Progress bar - Only show up to the dot position for active level */}
                        {shouldShowProgressBar(score, level.level) && (
                          <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: isActiveLevel ? `${progressWidth}%` : '100%',
                            backgroundColor: level.color,
                            opacity: isActiveLevel ? 1 : 0.7,
                            zIndex: 1
                          }}></div>
                        )}
                        
                        {/* Diamond marker at the end of the progress bar */}
                        {isActiveLevel && (
                          <div style={{
                            position: 'absolute',
                            zIndex: 5,
                            left: `${progressWidth}%`,
                            top: '50%',
                            width: '16px',
                            height: '16px',
                            backgroundColor: '#D2BA7B', /* Gold from brand colors */
                            transform: 'translate(-50%, -50%) rotate(45deg)',
                            border: '2px solid #243444', /* Navy border */
                            boxShadow: '0 0 5px rgba(0,0,0,0.5)'
                          }}>
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td 
                    style={{
                      ...scorecardStyles.td, 
                      ...scorecardStyles.scoreCell, 
                      backgroundColor: getColorForScore(score),
                      color: getTextColorForBackground(getMaturityLevelForScore(score).level)
                    }}
                  >
                    <strong>{Math.min(scorePercentage, 100)}%</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <div style={scorecardStyles.legend} className="scorecard-section legend-container">
        <h4 style={scorecardStyles.legendTitle}>{t('reports.maturityLevels')}</h4>
        <div style={scorecardStyles.legendItems} className="legend-items">
          {maturityLevels.map((level) => (
            <div key={level.level} style={scorecardStyles.legendItem} className="legend-item">
              <div style={{...scorecardStyles.legendColor, backgroundColor: level.color}} className="legend-color"></div>
              <div style={scorecardStyles.legendText} className="legend-text">
                <strong style={scorecardStyles.legendTextTitle}>{level.name}</strong>
                <span style={scorecardStyles.legendTextRange}>{level.range[0]}% - {level.range[1]-1}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Questions, Answers, and Notes Section */}
      <div style={{
        marginTop: '30px',
        pageBreakBefore: 'always',
        backgroundColor: '#FFFAFA',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '15px'
      }} className="questions-answers-section">
        <h3 style={{
          color: '#243444',
          marginBottom: '15px',
          fontSize: '18px',
          textAlign: 'center'
        }}>{t('reports.questionsAnswersNotes')}</h3>
        
        {data.responses && Array.isArray(data.responses) && data.responses.length > 0 ? (
          <div>
            {(() => {
              // Group responses by category
              const responsesByCategory = {};
              
              data.responses.forEach(response => {
                if (response && response.category) {
                  if (!responsesByCategory[response.category]) {
                    responsesByCategory[response.category] = [];
                  }
                  responsesByCategory[response.category].push(response);
                }
              });
              
              // Return an array of category sections
              return Object.keys(responsesByCategory).map(category => {
                const responses = responsesByCategory[category];
                
                return (
                  <div key={category} style={{
                    marginBottom: '20px',
                    pageBreakInside: 'avoid'
                  }}>
                    <h4 style={{
                      color: '#243444',
                      backgroundColor: '#f5f5f5',
                      padding: '8px 10px',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      fontSize: '16px'
                    }}>{category}</h4>
                    
                    {responses.map((response, index) => {
                      // Get the maturity level text based on the answer value
                      let answerText = '';
                      let levelName = '';
                      
                      // Find the maturity level name based on the answer
                      const answerValue = parseInt(response.answer, 10);
                      const maturityLevel = maturityLevels.find(level => level.level === answerValue);
                      levelName = maturityLevel ? maturityLevel.name : 'Unknown';
                      
                      switch(response.answer) {
                        case '0':
                          answerText = 'No evidence or capability exists to ensure this practice.';
                          break;
                        case '1':
                          answerText = 'This practice occurs sporadically and informally without documentation or consistency.';
                          break;
                        case '2':
                          answerText = 'This practice is documented and repeatable in some areas but lacks enterprise-wide adoption.';
                          break;
                        case '3':
                          answerText = 'This practice is consistently performed, tracked, and resourced across most of the organization.';
                          break;
                        case '4':
                          answerText = 'This practice fully meets industry and regulatory standards and is regularly audited for effectiveness.';
                          break;
                        case '5':
                          answerText = 'This practice is continuously improved, benchmarked, and embedded in strategic decision-making.';
                          break;
                        default:
                          answerText = 'No answer provided';
                      }
                      
                      return (
                        <div key={response.questionId || index} style={{
                          marginBottom: '15px',
                          padding: '10px',
                          border: '1px solid #eee',
                          borderRadius: '4px',
                          backgroundColor: '#fff'
                        }}>
                          <div style={{
                            marginBottom: '8px',
                            fontWeight: 'bold',
                            color: '#243444'
                          }}>
                            <span style={{ marginRight: '5px' }}>{index + 1}.</span>
                            {response.question}
                          </div>
                          
                          <div style={{
                            marginBottom: '8px',
                            padding: '5px 10px',
                            backgroundColor: getColorForScore(response.answer * 20),
                            color: getTextColorForBackground(getMaturityLevelForScore(response.answer * 20).level),
                            borderRadius: '4px'
                          }}>
                            <strong>{t('reports.answer')}:</strong> {answerText} ({levelName})
                          </div>
                          
                          {response.notes && (
                            <div style={{
                              padding: '5px 10px',
                              backgroundColor: '#f9f9f9',
                              borderRadius: '4px'
                            }}>
                              <strong>{t('reports.notes')}:</strong> {response.notes}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#4C5454' }}>
            {t('reports.noResponsesAvailable')}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaturityScorecard;
